/**
 * GTN Product Single — frontend interactivity.
 *
 * Handles:
 *   1. Gallery: thumbnail clicks switch the main image.
 *   2. Variations: attribute chip selection finds a matching variation,
 *      updates the price display, stock state, and variation image.
 *   3. Quantity: +/− buttons control the number input.
 *   4. Add to Cart: posts to the WooCommerce Store API v1.
 *   5. Description: accordion toggle.
 *
 * Security notes:
 *   - All HTML inserted via innerHTML uses only server-supplied,
 *     already-escaped priceHtml values from the variation JSON.
 *   - The Store API nonce is generated server-side via wp_create_nonce().
 *   - No user-supplied strings are ever written to innerHTML.
 */
( function () {
	'use strict';

	// ── Helpers ────────────────────────────────────────────────────────────

	/**
	 * Validate an http/https URL; return '#' for anything else.
	 *
	 * @param {string} str
	 * @return {string}
	 */
	function safeUrl( str ) {
		try {
			const u = new URL( String( str ) );
			return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : '#';
		} catch {
			return '#';
		}
	}

	// ── Per-block initialisation ───────────────────────────────────────────

	function initProduct( root ) {
		// ── Data attributes ───────────────────────────────────────────────

		const productId   = parseInt( root.dataset.productId, 10 );
		const productType = root.dataset.productType || 'simple';
		let   storeNonce  = root.dataset.storeNonce  || '';
		const apiRoot     = ( root.dataset.apiRoot   || '/wp-json/' ).replace( /\/$/, '' );
		const isVariable  = productType === 'variable';
		const labels      = {
			selectOptions: root.dataset.labelSelectOptions || '',
			addToCart: root.dataset.labelAddToCart || '',
			outOfStock: root.dataset.labelOutOfStock || '',
			selectBeforeAdd: root.dataset.labelSelectBeforeAdd || '',
			adding: root.dataset.labelAdding || '',
			addFailed: root.dataset.labelAddFailed || '',
			addedSuccess: root.dataset.labelAddedSuccess || '',
			addError: root.dataset.labelAddError || '',
		};

		// ── Load variation JSON ───────────────────────────────────────────

		let variations = [];

		if ( isVariable ) {
			const variationsId = root.dataset.variationsId;
			if ( variationsId ) {
				const scriptEl = variationsId ? document.getElementById( variationsId ) : null;
				if ( scriptEl ) {
					try {
						variations = JSON.parse( scriptEl.textContent );
					} catch {
						// Fallback: no variation data; chips still work but won't match.
					}
				}
			}
		}

		// ── DOM refs ──────────────────────────────────────────────────────

		const mainImageWrap = root.querySelector( '[data-gtn-main-image]' );
		const thumbsWrap    = root.querySelector( '[data-gtn-thumbs]' );
		const priceEl       = root.querySelector( '[data-gtn-price]' );
		const variationsEl  = root.querySelector( '[data-gtn-variations]' );
		const qtyInput      = root.querySelector( '[data-gtn-qty]' );
		const qtyDecBtn     = root.querySelector( '[data-gtn-qty-dec]' );
		const qtyIncBtn     = root.querySelector( '[data-gtn-qty-inc]' );
		const addToCartBtn  = root.querySelector( '[data-gtn-add-to-cart]' );
		const feedbackEl    = root.querySelector( '[data-gtn-feedback]' );
		const descToggle    = root.querySelector( '[data-gtn-desc-toggle]' );
		const descBody      = root.querySelector( '[data-gtn-desc-body]' );
		const attrGroups    = variationsEl
			? Array.from( variationsEl.querySelectorAll( '.gtn-product__attr-group' ) )
			: [];

		// ── State ─────────────────────────────────────────────────────────

		const selectedAttrs = {}; // { 'pa_color': 'red', 'pa_size': 'm' }
		let   currentVariationId = null;
		const initiallyInStock = addToCartBtn ? ! addToCartBtn.disabled : true;

		// ── Gallery ───────────────────────────────────────────────────────

		/**
		 * Show the image with the given imageId; hide all others.
		 * Also syncs the thumbnail active state.
		 *
		 * @param {number|string} imageId
		 */
		function switchImage( imageId ) {
			if ( ! mainImageWrap ) return;

			const id = String( imageId );

			mainImageWrap.querySelectorAll( '.gtn-product__main-img' ).forEach( ( img ) => {
				img.classList.toggle( 'is-active', img.dataset.imageId === id );
			} );

			if ( thumbsWrap ) {
				thumbsWrap.querySelectorAll( '.gtn-product__thumb' ).forEach( ( btn ) => {
					const active = btn.dataset.imageId === id;
					btn.classList.toggle( 'is-active', active );
					btn.setAttribute( 'aria-pressed', active ? 'true' : 'false' );
				} );
			}
		}

		if ( thumbsWrap ) {
			thumbsWrap.addEventListener( 'click', ( e ) => {
				const thumb = e.target.closest( '.gtn-product__thumb' );
				if ( thumb ) {
					switchImage( thumb.dataset.imageId );
				}
			} );
		}

		// ── Variation matching ─────────────────────────────────────────────

		/**
		 * Find the first variation whose attributes all match selectedAttrs.
		 * Variations with an empty attribute value accept any selected value.
		 *
		 * @return {Object|null}
		 */
		function findVariation() {
			if ( ! variations.length ) return null;

			return (
				variations.find( ( v ) => {
					return Object.entries( v.attributes ).every( ( [ rawKey, val ] ) => {
						// Normalize Woo keys for robust matching.
						const keyRaw = String( rawKey );
						const slug = keyRaw.replace( /^attribute_/, '' );
						const valStr = String( val || '' );
						const selected =
							selectedAttrs[ slug ]
							|| selectedAttrs[ keyRaw ]
							|| selectedAttrs[ 'attribute_' + slug ]
							|| '';
						// Empty value means "any".
						if ( ! valStr ) return true;
						return selected === valStr ||
							selected === slug ||
							selected.toLowerCase() === valStr.toLowerCase() ||
							slugify( selected ) === slugify( valStr );
					} );
				} ) || null
			);
		}

		function slugify( value ) {
			return String( value || '' )
				.toLowerCase()
				.trim()
				.replace( /\s+/g, '-' )
				.replace( /[^a-z0-9_-]/g, '' );
		}

		/**
		 * After attribute selection changes: find the matching variation,
		 * update price HTML, image, and add-to-cart button state.
		 */
		function updateVariation() {
			const v = findVariation();
			currentVariationId = v ? v.id : null;

			if ( ! isVariable ) {
				return;
			}

			// Price — swap in the variation's price HTML (already wp_kses_post'd server-side).
			if ( priceEl && v && v.priceHtml ) {
				priceEl.innerHTML = v.priceHtml;
			}

			// Image — switch to the variation image if it has one.
			if ( v && v.imageId ) {
				switchImage( v.imageId );
			}

			// Stock / button state.
			if ( addToCartBtn ) {
				if ( ! v ) {
					addToCartBtn.disabled = true;
					addToCartBtn.textContent = labels.selectOptions;
					return;
				}

				addToCartBtn.disabled = ! v.inStock;
				addToCartBtn.textContent = v.inStock ? labels.addToCart : labels.outOfStock;
			}
		}

		function hasAllSelections() {
			if ( ! attrGroups.length ) return true;

			return attrGroups.every( ( group ) => {
				return !! group.querySelector( '.gtn-product__chip[aria-pressed="true"], .gtn-product__chip.is-selected' );
			} );
		}

		function updateSimpleSelectionState() {
			if ( ! addToCartBtn || isVariable ) return;

			if ( ! initiallyInStock ) {
				addToCartBtn.disabled = true;
				addToCartBtn.textContent = labels.outOfStock;
				return;
			}

			if ( hasAllSelections() ) {
				addToCartBtn.disabled = false;
				addToCartBtn.textContent = labels.addToCart;
			} else {
				addToCartBtn.disabled = true;
				addToCartBtn.textContent = labels.selectOptions;
			}
		}

		function syncInitialSelections() {
			if ( ! variationsEl ) return;

			variationsEl.querySelectorAll( '.gtn-product__attr-group' ).forEach( ( group ) => {
				const selectedChip = group.querySelector(
					'.gtn-product__chip[aria-pressed="true"], .gtn-product__chip.is-selected'
				);

				if ( ! selectedChip ) return;

				const attrSlug = group.dataset.attrName;
				selectedAttrs[ attrSlug ] = selectedChip.dataset.value || '';

				selectedChip.classList.add( 'is-selected' );
				selectedChip.setAttribute( 'aria-pressed', 'true' );

				const valueDisplay = group.querySelector( '[data-gtn-attr-value]' );
				if ( valueDisplay && ! valueDisplay.textContent ) {
					valueDisplay.textContent = selectedChip.dataset.label || '';
				}
			} );
		}

		if ( variationsEl ) {
			variationsEl.addEventListener( 'click', ( e ) => {
				const chip = e.target.closest( '.gtn-product__chip' );
				if ( ! chip ) return;

				const group = chip.closest( '.gtn-product__attr-group' );
				if ( ! group ) return;

				// Remove selection from sibling chips in this group.
				group.querySelectorAll( '.gtn-product__chip' ).forEach( ( c ) => {
					c.classList.remove( 'is-selected' );
					c.setAttribute( 'aria-pressed', 'false' );
				} );

				chip.classList.add( 'is-selected' );
				chip.setAttribute( 'aria-pressed', 'true' );

				// Store the selected attribute value using the raw slug (no prefix).
				const attrSlug = group.dataset.attrName; // e.g. 'pa_color'
				selectedAttrs[ attrSlug ] = chip.dataset.value || '';

				// Show the human-readable label next to the attribute name.
				const valueDisplay = group.querySelector( '[data-gtn-attr-value]' );
				if ( valueDisplay ) {
					valueDisplay.textContent = chip.dataset.label || '';
				}

				if ( isVariable ) {
					updateVariation();
				} else {
					updateSimpleSelectionState();
				}
			} );
		}

		syncInitialSelections();

		if ( isVariable ) {
			updateVariation();
		} else {
			updateSimpleSelectionState();
		}

		// ── Quantity ───────────────────────────────────────────────────────

		function getQty() {
			const raw = qtyInput ? parseInt( qtyInput.value, 10 ) : 1;
			return Number.isFinite( raw ) && raw >= 1 ? raw : 1;
		}

		if ( qtyDecBtn ) {
			qtyDecBtn.addEventListener( 'click', () => {
				const q = getQty();
				if ( qtyInput && q > 1 ) {
					qtyInput.value = String( q - 1 );
				}
			} );
		}

		if ( qtyIncBtn ) {
			qtyIncBtn.addEventListener( 'click', () => {
				if ( qtyInput ) {
					qtyInput.value = String( getQty() + 1 );
				}
			} );
		}

		// Prevent non-numeric keyboard input from setting qty < 1.
		if ( qtyInput ) {
			qtyInput.addEventListener( 'change', () => {
				const v = parseInt( qtyInput.value, 10 );
				if ( ! Number.isFinite( v ) || v < 1 ) {
					qtyInput.value = '1';
				}
			} );
		}

		// ── Feedback ───────────────────────────────────────────────────────

		/**
		 * Display a feedback message for 5 seconds then hide it.
		 * The message text is generated by this code — never from external
		 * sources — so it is safe to set via textContent.
		 *
		 * @param {string}  msg
		 * @param {boolean} isError
		 */
		function showFeedback( msg, isError ) {
			if ( ! feedbackEl ) return;
			feedbackEl.className =
				'gtn-product__feedback ' + ( isError ? 'is-error' : 'is-success' );
			feedbackEl.textContent = msg;
			feedbackEl.hidden = false;

			setTimeout( () => {
				feedbackEl.hidden = true;
				feedbackEl.textContent = '';
				feedbackEl.className = 'gtn-product__feedback';
			}, 5000 );
		}

		// ── Add to Cart ────────────────────────────────────────────────────

		if ( addToCartBtn ) {
			addToCartBtn.addEventListener( 'click', async () => {
				const qty = getQty();

				// For variable products, require all attributes to be selected.
				let itemId = productId;

				if ( ! hasAllSelections() ) {
					showFeedback( labels.selectBeforeAdd, true );
					return;
				}

				if ( isVariable ) {
					if ( ! currentVariationId ) {
						showFeedback( labels.selectBeforeAdd, true );
						return;
					}
					// Use the variation ID (a WC_Product_Variation post ID).
					itemId = currentVariationId;
				}

				addToCartBtn.disabled = true;
				addToCartBtn.classList.add( 'is-loading' );
				addToCartBtn.textContent = labels.adding;

				const requestBody = {
					id: itemId,
					quantity: qty,
				};

				if ( ! isVariable && Object.keys( selectedAttrs ).length ) {
					requestBody.gtn_selected_attributes = { ...selectedAttrs };
				}

				try {
					const res = await fetch( apiRoot + '/wc/store/v1/cart/add-item', {
						method: 'POST',
						credentials: 'same-origin',
						headers: {
							'Content-Type': 'application/json',
							Nonce: storeNonce,
							'X-WC-Store-API-Nonce': storeNonce,
						},
						body: JSON.stringify( requestBody ),
					} );

					// Refresh nonce from response header for subsequent requests.
					const freshNonce = res.headers.get( 'Nonce' ) || res.headers.get( 'X-WC-Store-API-Nonce' );
					if ( freshNonce ) {
						storeNonce = freshNonce;
					}

					if ( ! res.ok ) {
						const errData = await res.json().catch( () => ( {} ) );
						throw new Error( errData.message || labels.addFailed );
					}

					const data = await res.json();

					// Update cart-count badges anywhere in the page.
					const cartCount = typeof data.items_count === 'number' ? data.items_count : null;
					if ( cartCount !== null ) {
						document
							.querySelectorAll( '[data-wc-cart-count]' )
							.forEach( ( el ) => {
								el.textContent = String( cartCount );
							} );
					}

					showFeedback( labels.addedSuccess, false );
				} catch ( err ) {
					showFeedback(
						err instanceof Error ? err.message : labels.addError,
						true
					);
				} finally {
					addToCartBtn.classList.remove( 'is-loading' );

					if ( isVariable ) {
						updateVariation();
					} else {
						updateSimpleSelectionState();
					}
				}
			} );
		}

		// ── Description accordion ─────────────────────────────────────────

		if ( descToggle && descBody ) {
			descToggle.addEventListener( 'click', () => {
				const expanded = descToggle.getAttribute( 'aria-expanded' ) === 'true';
				descToggle.setAttribute( 'aria-expanded', String( ! expanded ) );
				descBody.hidden = expanded;
			} );
		}
	}

	// ── Boot ───────────────────────────────────────────────────────────────

	document.querySelectorAll( '[data-gtn-product]' ).forEach( initProduct );
} )();
